import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { logger } from './lib/utils/logger';
import './index.css';
// Design System CSS는 index.css 다음에 직접 import하여 코드 스플리팅 페이지의
// 외부 라이브러리 CSS(예: @xyflow/react)보다 높은 우선순위를 보장
import './styles/design-system.css';
import LoginPage from './pages/LoginPage';
import { PageLoader } from './components/PageLoader';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { TabProvider, useTab } from './contexts/TabContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { useAuth } from './lib/hooks/useAuth';
import { useTenant } from './lib/hooks/useTenant';
import { useSessionTimeout } from './lib/hooks/useSessionTimeout';
import { defaultAccountAPI } from './lib/services/accountApi';
import { showError } from './lib/services/errorService';
import { ROLE_DISPLAY_NAMES, AUTH_MESSAGES } from './constants/authMessages';
import { PasswordChangeModal } from './components/auth/PasswordChangeModal';
import { SessionTimeoutModal } from './components/auth/SessionTimeoutModal';
import { TabBar } from './components/common/TabBar';
import { TabContainer } from './components/common/TabContainer';
import { toast } from 'react-toastify';
import ErrorBoundary from './components/common/ErrorBoundary';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Only WorkflowManualPage remains lazy-loaded here (popup, outside tab system)
const WorkflowManualPage = lazy(() => import('./pages/WorkflowManualPage'));

// Navigation Icons (thin-line style)

const HomeIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="18"
    height="18"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const ManualIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="18"
    height="18"
  >
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <path d="M8 7h8M8 11h8M8 15h5" />
  </svg>
);

const AdminIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="18"
    height="18"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const BuilderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="18"
    height="18"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75a4.5 4.5 0 01-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 11-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 016.336-4.486l-3.276 3.276a3.004 3.004 0 002.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.867 19.125h.008v.008h-.008v-.008z" />
  </svg>
);

const OperationsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="18"
    height="18"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
    />
  </svg>
);

const LabsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="18"
    height="18"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M5 14.5l-.94.94a1.5 1.5 0 00.44 2.428l.56.224a8.25 8.25 0 007.18.112l.264-.113a1.875 1.875 0 011.68.075l.274.164a1.5 1.5 0 001.378.06l1.22-.488A1.5 1.5 0 0019.8 15.3M5 14.5l2.25 2.25"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="12"
    height="12"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const LogoutIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="16"
    height="16"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const UserIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="16"
    height="16"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const KeyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    width="16"
    height="16"
  >
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.778-7.778zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

// Reusable Nav Dropdown Component
const NavDropdown = ({ icon: Icon, label, isActive, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="ds-nav-dropdown" ref={dropdownRef}>
      <button
        className={`ds-nav-link ds-nav-dropdown-trigger ${isActive ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon />
        {label}
        <ChevronDownIcon />
      </button>
      {isOpen && (
        <div className="ds-nav-dropdown-menu" onClick={() => setIsOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
};

// Builder Dropdown (all authenticated users, System Prompts requires TENANT_ADMIN+)
const BuilderDropdown = () => {
  const location = useLocation();
  const { isTenantAdminOrHigher } = useAuth();
  const { openTab } = useTab();
  const builderPaths = [
    '/admin/system-prompts',
    '/variable-manager',
    '/tool-manager',
    '/workflow-editor',
  ];
  const isActive = builderPaths.some((p) => location.pathname.startsWith(p));

  return (
    <NavDropdown icon={BuilderIcon} label="Builder" isActive={isActive}>
      {isTenantAdminOrHigher() && (
        <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/system-prompts')}>
          Prompt Studio
        </button>
      )}
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/variable-manager')}>
        Variables
      </button>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/tool-manager')}>
        Tools
      </button>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/workflow-editor')}>
        Workflows
      </button>
    </NavDropdown>
  );
};

// Monitoring Dropdown (TENANT_ADMIN+ only)
const OperationsDropdown = () => {
  const location = useLocation();
  const { isTenantAdminOrHigher } = useAuth();
  const { openTab } = useTab();
  const opsPaths = [
    '/admin/analytics',
    '/admin/channels',
    '/admin/sessions',
    '/admin/conversations',
    '/admin/monitoring',
    '/admin/wallboard',
    '/admin/service-analytics',
  ];
  const isActive =
    opsPaths.some((p) => location.pathname.startsWith(p)) ||
    (location.pathname === '/admin' && !location.pathname.startsWith('/admin/'));

  if (!isTenantAdminOrHigher()) return null;

  return (
    <NavDropdown icon={OperationsIcon} label="Monitoring" isActive={isActive}>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin')}>
        대시보드
      </button>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/wallboard')}>
        실시간 현황판
      </button>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/sessions')}>
        실시간 세션
      </button>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/conversations')}>
        대화 로그
      </button>
      <div className="ds-nav-dropdown-divider" />
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/service-analytics')}>
        서비스 분석
      </button>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/channels')}>
        채널 분석
      </button>
    </NavDropdown>
  );
};

// Admin Dropdown (TENANT_ADMIN+ only)
const AdminDropdown = () => {
  const location = useLocation();
  const { isTenantAdminOrHigher, isGlobalAdmin } = useAuth();
  const { openTab } = useTab();
  const adminPaths = [
    '/admin/tenants',
    '/admin/announcements',
    '/admin/users',
    '/admin/permissions',
    '/admin/llm-models',
    '/admin/audit-logs',
  ];
  const isActive = adminPaths.some((p) => location.pathname.startsWith(p));

  if (!isTenantAdminOrHigher()) return null;

  return (
    <NavDropdown icon={AdminIcon} label="Admin" isActive={isActive}>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/tenants')}>
        테넌트 · 채널
      </button>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/announcements')}>
        공지사항
      </button>
      {isGlobalAdmin() && (
        <>
          <div className="ds-nav-dropdown-divider" />
          <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/users')}>
            사용자 관리
          </button>
          <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/permissions')}>
            권한 관리
          </button>
          <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/llm-models')}>
            LLM 모델 관리
          </button>
          <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/audit-logs')}>
            감사 로그
          </button>
        </>
      )}
    </NavDropdown>
  );
};

// Labs Dropdown (all authenticated users, TTS requires TENANT_ADMIN+)
const LabsDropdown = () => {
  const location = useLocation();
  const { isTenantAdminOrHigher } = useAuth();
  const { openTab } = useTab();
  const labsPaths = ['/chat', '/admin/chat-tts', '/workflow-test', '/orchestrator-chat'];
  const isActive = labsPaths.some((p) => location.pathname.startsWith(p));

  return (
    <NavDropdown icon={LabsIcon} label="Labs" isActive={isActive}>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/chat')}>
        Model Playground
      </button>
      {isTenantAdminOrHigher() && (
        <button className="ds-nav-dropdown-item" onClick={() => openTab('/admin/chat-tts')}>
          TTS Playground
        </button>
      )}
      <div className="ds-nav-dropdown-divider" />
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/workflow-test')}>
        Workflow Tester
      </button>
      <button className="ds-nav-dropdown-item" onClick={() => openTab('/orchestrator-chat')}>
        Orchestrator Sandbox
      </button>
    </NavDropdown>
  );
};

// User Info & Logout Component (with Tenant Selector for GLOBAL_ADMIN)
const UserMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { effectiveTenantId, selectTenant, canSelectTenant } = useTenant();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load tenants when dropdown opens (for GLOBAL_ADMIN)
  useEffect(() => {
    if (isOpen && canSelectTenant && tenants.length === 0) {
      loadTenants();
    }
  }, [isOpen, canSelectTenant, tenants.length]);

  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      const data = await defaultAccountAPI.getAllTenants();
      setTenants(data || []);
    } catch (err) {
      logger.error('Failed to load tenants:', err);
      showError(err, '테넌트 목록을 불러오지 못했습니다.');
    } finally {
      setTenantsLoading(false);
    }
  };

  const handleTenantSelect = (tenantId) => {
    selectTenant(tenantId);
  };

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate('/login');
  };

  const handlePasswordChange = () => {
    setIsOpen(false);
    setIsPasswordModalOpen(true);
  };

  const handleOpenManual = () => {
    setIsOpen(false);
    // 팝업 창 설정: 가로 1200px, 세로 800px, 중앙 정렬
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      '/workflow-manual',
      'workflow-manual-popup',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <div className="ds-user-menu" ref={dropdownRef}>
        <button
          className="ds-user-menu-trigger"
          onClick={() => setIsOpen(!isOpen)}
          title={user.name}
        >
          <UserIcon />
        </button>
        {isOpen && (
          <div className="ds-user-dropdown">
            <div className="ds-user-info">
              <span className="ds-user-email">{user.email}</span>
              <span className="ds-user-role">{ROLE_DISPLAY_NAMES[user.role] || user.role}</span>
            </div>
            {/* Tenant Selector for GLOBAL_ADMIN */}
            {canSelectTenant && (
              <>
                <div className="ds-user-dropdown-divider" />
                <div className="ds-user-tenant-section">
                  <span className="ds-user-tenant-label">테넌트</span>
                  {tenantsLoading ? (
                    <span className="ds-user-tenant-loading">로딩...</span>
                  ) : (
                    <select
                      className="ds-user-tenant-select"
                      value={effectiveTenantId || ''}
                      onChange={(e) => handleTenantSelect(e.target.value)}
                    >
                      {tenants.map((tenant) => {
                        const tenantId = tenant.tenantId || tenant.id;
                        return (
                          <option key={tenantId} value={tenantId}>
                            {tenant.tenantName || tenant.name || tenantId}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </div>
              </>
            )}
            <div className="ds-user-dropdown-divider" />
            <button className="ds-user-dropdown-item" onClick={handleOpenManual}>
              <ManualIcon />
              사용 매뉴얼
            </button>
            <button className="ds-user-dropdown-item" onClick={handlePasswordChange}>
              <KeyIcon />
              {AUTH_MESSAGES.PASSWORD_CHANGE_BUTTON}
            </button>
            <div className="ds-user-dropdown-divider" />
            <button className="ds-user-dropdown-item" onClick={handleLogout}>
              <LogoutIcon />
              {AUTH_MESSAGES.LOGOUT_BUTTON}
            </button>
          </div>
        )}
      </div>
      <PasswordChangeModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </>
  );
};

// Session Timeout Configuration
const SESSION_CONFIG = {
  idleTimeout: 25 * 60 * 1000, // 25분 비활동 후 경고
  warningDuration: 5 * 60 * 1000, // 5분 카운트다운
};

// Session Timeout Wrapper Component
const SessionTimeoutWrapper = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const handleTimeout = async () => {
    toast.info(AUTH_MESSAGES.SESSION_TIMEOUT_AUTO_LOGOUT);
    await logout();
    navigate('/login');
  };

  const { isWarningVisible, remainingTime, extendSession, cancelWarning } = useSessionTimeout({
    idleTimeout: SESSION_CONFIG.idleTimeout,
    warningDuration: SESSION_CONFIG.warningDuration,
    onTimeout: handleTimeout,
    enabled: isAuthenticated,
  });

  const handleLogoutNow = async () => {
    cancelWarning();
    await logout();
    navigate('/login');
  };

  return (
    <>
      {children}
      <SessionTimeoutModal
        isOpen={isWarningVisible}
        remainingTime={remainingTime}
        onExtend={extendSession}
        onLogout={handleLogoutNow}
      />
    </>
  );
};

// Main Navigation Component (only for authenticated users)
const Navigation = () => {
  const { goHome } = useTab();

  return (
    <nav className="ds-navbar">
      <div className="ds-nav-links">
        <button className="ds-nav-logo" title="Home" onClick={goHome}>
          <HomeIcon />
        </button>
        <BuilderDropdown />
        <OperationsDropdown />
        <AdminDropdown />
        <LabsDropdown />
      </div>
      <div className="ds-nav-right">
        <UserMenu />
      </div>
    </nav>
  );
};

// Tabbed Layout — replaces MainLayout + AdminLayout
const TabbedLayout = () => {
  return (
    <TabProvider>
      <SessionTimeoutWrapper>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
          <Navigation />
          <TabBar />
          <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            <TabContainer />
          </div>
        </div>
      </SessionTimeoutWrapper>
    </TabProvider>
  );
};

const Root = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <Routes>
            {/* Public Route - Login */}
            <Route path="/login" element={<LoginPage />} />

            {/* Workflow Manual - No navigation bar (popup window) */}
            <Route element={<ProtectedRoute />}>
              <Route
                path="/workflow-manual"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <WorkflowManualPage />
                  </Suspense>
                }
              />
            </Route>

            {/* All authenticated routes — single TabbedLayout handles everything */}
            <Route element={<ProtectedRoute />}>
              <Route path="/*" element={<TabbedLayout />} />
            </Route>
          </Routes>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <Root />
    </ErrorBoundary>
  </React.StrictMode>
);
